import { useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTR = 'Tiles &copy; Esri';

const STRATA_PALETTE = [
  '#e41a1c', '#377eb8', '#4daf4a', '#ff7f00', '#984ea3',
  '#a65628', '#f781bf', '#aaaaaa', '#d4b400', '#00bcd4',
  '#8bc34a', '#ff5722', '#795548', '#607d8b', '#e91e63',
];
function strataColor(s) {
  if (s == null) return '#888';
  return STRATA_PALETTE[(Number(s) - 1) % STRATA_PALETTE.length];
}

function BoundsFitter({ geojson }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) return;
    try {
      const bounds = L.geoJSON(geojson).getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
    } catch (_) {}
  }, [geojson, map]);
  return null;
}

// Parse artifact payload_json
function parseFeatures(data) {
  const payload = data?.payload_json
    ? (typeof data.payload_json === 'string' ? JSON.parse(data.payload_json) : data.payload_json)
    : data;
  return payload?.features || (Array.isArray(payload) ? payload : []);
}

/**
 * MiniMapPreview — lightweight inline Leaflet map for showing boundary + strata + points.
 *
 * Props:
 *   boundary   — GeoJSON FeatureCollection (project/site boundary)
 *   strata     — GeoJSON FeatureCollection (strata polygons with .properties.stratum)
 *   points     — array of GeoJSON Point features
 *   height     — CSS height (default 300)
 */
export default function MiniMapPreview({ boundary, strata, points, height = 300 }) {
  const boundaryStyle = { fillColor: 'transparent', color: '#1976d2', weight: 2, dashArray: '6 4' };
  const strataStyle = (feature) => ({
    fillColor: strataColor(feature.properties?.stratum),
    fillOpacity: 0.55, color: 'rgba(255,255,255,0.5)', weight: 0.5,
  });

  const hasContent = boundary || strata?.features?.length || points?.length;

  return (
    <MapContainer
      center={[20, 15]} zoom={3}
      style={{ width: '100%', height, borderRadius: 8, border: '1px solid #e0e0e0' }}
      zoomControl={false}
      attributionControl={false}
    >
      <TileLayer url={SATELLITE_URL} attribution={SATELLITE_ATTR} />

      {boundary && (
        <>
          <BoundsFitter geojson={boundary} />
          <GeoJSON data={boundary} style={boundaryStyle} />
        </>
      )}

      {strata?.features?.length > 0 && (
        <GeoJSON
          key={`strata-${strata.features.length}`}
          data={strata}
          style={strataStyle}
        />
      )}

      {points?.map((f, i) => {
        const [lng, lat] = f.geometry.coordinates;
        return (
          <CircleMarker key={i} center={[lat, lng]} radius={4}
            pathOptions={{ color: '#fff', weight: 1.5, fillColor: strataColor(f.properties?.stratum), fillOpacity: 1 }}
          />
        );
      })}
    </MapContainer>
  );
}

// Re-export parseFeatures for use by StratificationPage
MiniMapPreview.parseFeatures = parseFeatures;
